import { useStaffMode } from "../../../hooks/StaffModeProvider";
import WSGCheck from "../../Atomos/Icons/WSGCheck";
import React from "react";

interface CheckboxProps {
  index: number;
  text: string;
  checked: boolean;
  handleCheckboxChange: (index: number) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  disable?: boolean;
  error?: boolean;
  errormessage?: string;
  removeClassName?: boolean;
  customClassName?: string;
  className?: string;
  icon?: JSX.Element;
}

const WSGCheckbox: React.FC<CheckboxProps> = ({
  inputRef,
  text,
  checked,
  index,
  handleCheckboxChange,
  disable,
  error,
  removeClassName,
  customClassName,
  className,
  icon,
}) => {
  const { isStaffMode } = useStaffMode();

  const labelBaseClasses =
    "flex items-center gap-[0.625rem] py-[0.625rem] lg:py-[0.81rem] align-middle text-lg leading-5 text-gray-font cursor-pointer";
  const labelClasses = React.useMemo(
    () =>
      `${removeClassName ? customClassName || "" : labelBaseClasses} ${className || ""}`.trim(),
    [removeClassName, customClassName, labelBaseClasses, className]
  );

  const checkboxStrokeClass = React.useMemo(
    () =>
      disable ? "stroke-gray-200" : error ? "stroke-primary" : "stroke-gray-font",
    [disable, error]
  );

  const checkboxFillClass = React.useMemo(() => {
    if (!checked) {
      return "fill-none";
    }
    if (disable) {
      return "fill-gray-200";
    }
    return isStaffMode ? "fill-secondary" : "fill-primary";
  }, [checked, disable, isStaffMode]);

  const checkboxContainerClasses = `absolute stroke-2 min-w-[24px] min-h-[24px] inset-0 ${checkboxStrokeClass} ${checkboxFillClass}`;

  const textColorClass = React.useMemo(
    () => (disable ? "text-gray-200" : "text-gray-font"),
    [disable]
  );
  const textClasses = `text-base leading-6 lg:text-lg lg:leading-8 ${textColorClass} hover:${isStaffMode ? "text-secondary" : "text-primary"} first-letter:uppercase`;

  return (
    <label className={labelClasses}>
      <input
        ref={inputRef}
        type="checkbox"
        className="hidden"
        onChange={() => handleCheckboxChange(index)}
        checked={checked}
        disabled={disable}
      />
      <div className="relative w-6 h-6">
        <div className={checkboxContainerClasses}>
          <WSGCheck check={checked} />
        </div>
      </div>
      {icon ? (
        <div className="flex flex-row flex-1 items-center gap-1">
          <span className="h-6 w-6">{icon}</span>
          <span className={textClasses}>{text}</span>
        </div>
      ) : (
        <div className="flex flex-1 flex-col">
          <span className={textClasses}>{text}</span>
        </div>
      )}
    </label>
  );
};

export default WSGCheckbox;
